// useSearchFilters.js
// Custom hook to manage all search/filter state for FreeAgentSearch
// Consolidates: searchTerm, activeTab, positionFilter, showAll, advancedFilters

import { useState, useCallback } from 'react';

/**
 * Custom hook for managing search filters and UI state
 * Consolidates 5 state variables into one organized hook
 * 
 * @returns {Object} Search filter state and setters
 */
export const useSearchFilters = ({ includeNon40Man: initialNon40Man = false } = {}) => {
  // Search & Tab State
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('hitting');
  const [positionFilter, setPositionFilter] = useState('all');
  const [viewMode, setViewMode] = useState('free_agents');
  const [showAll, setShowAll] = useState(false);
  const [statsYear, setStatsYear] = useState('current'); // 'current' or 'previous'
  const [includeNon40Man, setIncludeNon40Man] = useState(initialNon40Man);

  // Rolling-row visibility toggle. Default ON preserves the legacy two-row UX.
  // Ephemeral — resets on page reload (no localStorage / URL sync yet).
  const [showRecent, setShowRecent] = useState(true);

  // Advanced Filters State
  const [advancedFilters, setAdvancedFilters] = useState({
    // Hitter filters
    minAB: '',
    maxAB: '',
    minHR: '',
    maxHR: '',
    minG: '',
    maxG: '',
    minPrice: '',
    maxPrice: '',
    hitterQualified: false,
    
    // Pitcher filters
    minK: '',
    maxK: '',
    minGS: '',
    maxGS: '',
    minIP: '',
    maxIP: '',
    pitcherQualified: false,
    
    // Date filters
    dateRange: 'season',
    customStartDate: '',
    customEndDate: '',
    
    // MLB Qualification thresholds
    qualifiedPA: 502,
    qualifiedIP: 162
  });

  // Reset filters helper
  const resetFilters = useCallback(() => {
    setSearchTerm('');
    setPositionFilter('all');
    setAdvancedFilters({
      minAB: '',
      maxAB: '',
      minHR: '',
      maxHR: '',
      minG: '',
      maxG: '',
      minPrice: '',
      maxPrice: '',
      hitterQualified: false,
      minK: '',
      maxK: '',
      minGS: '',
      maxGS: '',
      minIP: '',
      maxIP: '',
      pitcherQualified: false,
      dateRange: 'season',
      customStartDate: '',
      customEndDate: '',
      qualifiedPA: 502,
      qualifiedIP: 162
    });
  }, []);

  return {
    // State values
    searchTerm,
    activeTab,
    positionFilter,
    viewMode,
    showAll,
    advancedFilters,
    statsYear,
    includeNon40Man,
    showRecent,

    // Setters
    setSearchTerm,
    setActiveTab,
    setPositionFilter,
    setViewMode,
    setShowAll,
    setAdvancedFilters,
    setStatsYear,
    setIncludeNon40Man,
    setShowRecent,

    // Helpers
    resetFilters
  };
};
